import { useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  LayoutDashboard, Users, Briefcase, Calendar, FileText, MessageSquare,
  Video, ChevronDown, ChevronRight, Settings, UserCircle, Scale, Plane,
  FileCheck, Calculator, PlusCircle, BarChart3
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface PartnerType {
  id: string;
  name: { en: string; fr: string };
  icon: any;
  basePath: string;
}

const partnerTypes: PartnerType[] = [
  { id: "business-law", name: { en: "Business Law", fr: "Droit des Affaires" }, icon: Scale, basePath: "/internal-partners/business-law" },
  { id: "immigration-law", name: { en: "Immigration Law", fr: "Droit de l'Immigration" }, icon: Plane, basePath: "/internal-partners/immigration-law" },
  { id: "notary", name: { en: "Notary", fr: "Notaire" }, icon: FileCheck, basePath: "/internal-partners/notary" },
  { id: "tax-advisor", name: { en: "Tax Advisor", fr: "Conseiller Fiscal" }, icon: Calculator, basePath: "/internal-partners/tax-advisor" },
  { id: "cpa", name: { en: "CPA (Accountant)", fr: "CPA (Comptable)" }, icon: BarChart3, basePath: "/internal-partners/cpa" },
];

const getNavigationItems = (basePath: string, t: any) => [
  { title: t.overview, url: basePath, icon: LayoutDashboard, end: true },
  { title: t.clients, url: `${basePath}/clients`, icon: Users },
  { title: t.mandates, url: `${basePath}/mandates`, icon: Briefcase },
  { title: t.calendar, url: `${basePath}/calendar`, icon: Calendar },
  { title: t.documents, url: `${basePath}/documents`, icon: FileText },
  { title: t.messages, url: `${basePath}/messages`, icon: MessageSquare },
  { title: t.videoConference, url: `${basePath}/video`, icon: Video },
  { title: t.analytics, url: `${basePath}/analytics`, icon: BarChart3 },
];

const translations = {
  en: {
    partnerServices: "Partner Services",
    overview: "Overview",
    clients: "My Clients",
    mandates: "Mandates",
    calendar: "Calendar",
    documents: "Documents",
    messages: "Messages",
    videoConference: "Video Conference",
    newMandate: "New Mandate",
    analytics: "Analytics",
    settings: "Settings",
    profile: "My Profile",
    allPartners: "All Partners Overview",
  },
  fr: {
    partnerServices: "Services Partenaires",
    overview: "Aperçu",
    clients: "Mes Clients",
    mandates: "Mandats",
    calendar: "Calendrier",
    documents: "Documents",
    messages: "Messages",
    videoConference: "Visioconférence",
    newMandate: "Nouveau Mandat",
    analytics: "Analytique",
    settings: "Paramètres",
    profile: "Mon Profil",
    allPartners: "Vue d'ensemble Partenaires",
  },
};

interface InternalPartnerSidebarProps {
  partnerType?: string;
}

export function InternalPartnerSidebar({ partnerType }: InternalPartnerSidebarProps) {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const currentPath = location.pathname;

  const [expandedPartner, setExpandedPartner] = useState<string | null>(partnerType || null);

  const isActive = (path: string, end?: boolean) => {
    if (end) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const currentPartner = partnerTypes.find(p => p.id === partnerType);

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        {/* Partner Types with Sub-navigation */}
        {partnerTypes.map((partner) => {
          const navItems = getNavigationItems(partner.basePath, t);
          const isExpanded = expandedPartner === partner.id || isActive(partner.basePath);
          const partnerName = partner.name[language as keyof typeof partner.name] || partner.name.en;

          return (
            <SidebarGroup key={partner.id}>
              <Collapsible open={isExpanded} onOpenChange={(open) => setExpandedPartner(open ? partner.id : null)}>
                <CollapsibleTrigger asChild>
                  <SidebarGroupLabel className="cursor-pointer flex items-center justify-between hover:bg-muted/50 rounded-md p-2 transition-colors">
                    <div className="flex items-center gap-2">
                      <partner.icon className="h-4 w-4" />
                      {state !== "collapsed" && <span>{partnerName}</span>}
                    </div>
                    {state !== "collapsed" && (
                      isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                    )}
                  </SidebarGroupLabel>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {navItems.map((item) => {
                        const active = isActive(item.url, item.end);
                        return (
                          <SidebarMenuItem key={item.url}>
                            <SidebarMenuButton asChild isActive={active}>
                              <NavLink
                                to={item.url}
                                className={cn(
                                  "pl-6",
                                  active
                                    ? "bg-primary/10 text-primary font-medium"
                                    : "hover:bg-muted/50"
                                )}
                              >
                                <item.icon className="h-4 w-4" />
                                {state !== "collapsed" && <span>{item.title}</span>}
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                      {/* Quick Actions */}
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={`${partner.basePath}/new-mandate`}
                            className="pl-6 text-primary hover:bg-primary/10"
                          >
                            <PlusCircle className="h-4 w-4" />
                            {state !== "collapsed" && <span>{t.newMandate}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          );
        })}

        {/* Settings & Profile */}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={currentPath === "/partners/profile"}>
                  <NavLink to="/partners/profile">
                    <UserCircle className="h-4 w-4" />
                    {state !== "collapsed" && <span>{t.profile}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={currentPath === "/settings"}>
                  <NavLink to="/settings">
                    <Settings className="h-4 w-4" />
                    {state !== "collapsed" && <span>{t.settings}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
